import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { SpellCheck, Check, X, Loader2 } from "lucide-react";

/**
 * Ikonknapp som kjører AI-staveretter på en tekstverdi og lar brukeren
 * godta/avvise korreksjonen.
 *
 * Props:
 *   - value: string — teksten som skal sjekkes
 *   - onAccept: (correctedText) => void — kalles når brukeren godtar endringer
 *   - context: "generell" | "oppskrift" | "kalender" | "todo" | "notat"
 *   - testid: string (optional) — for e2e-tester
 *   - label: string (optional) — tekst ved siden av ikonet. Default ingen.
 *   - size: "xs" | "sm" (optional) — default "sm"
 */
export default function SpellCheckButton({ value, onAccept, context = "generell", testid, label, size = "sm", disabled }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [open, setOpen] = useState(false);

  const run = async () => {
    const text = (value || "").trim();
    if (!text) { toast.error("Skriv litt tekst først"); return; }
    if (text.length < 3) { toast.error("For kort tekst"); return; }
    setLoading(true);
    try {
      const res = await api.spellCheck(text, context);
      setResult(res.data);
      setOpen(true);
      if (!res.data?.changed) {
        toast.success("Ingen stavefeil funnet ✓");
        setOpen(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Staveretter feilet");
    } finally {
      setLoading(false);
    }
  };

  const accept = () => {
    if (result?.corrected) {
      onAccept?.(result.corrected);
      toast.success("Tekst oppdatert");
    }
    setOpen(false);
    setResult(null);
  };

  const dismiss = () => {
    setOpen(false);
    setResult(null);
  };

  const btnCls = size === "xs"
    ? "inline-flex items-center gap-1 rounded-md p-1 text-xs text-stone-400 hover:text-amber-700 hover:bg-amber-50"
    : "inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-stone-500 hover:text-amber-700 hover:bg-amber-50 border border-stone-200 hover:border-amber-300";
  const iconCls = size === "xs" ? "h-3.5 w-3.5" : "h-3.5 w-3.5";

  return (
    <>
      <button
        type="button"
        onClick={run}
        disabled={loading || disabled || !(value || "").trim()}
        className={`${btnCls} disabled:opacity-40 disabled:cursor-not-allowed transition-colors`}
        data-testid={testid || "spell-check-btn"}
        title="Sjekk staving med AI"
      >
        {loading ? <Loader2 className={`${iconCls} animate-spin`} /> : <SpellCheck className={iconCls} />}
        {label && <span>{label}</span>}
      </button>

      <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
        <DialogContent className="max-w-lg" data-testid="spell-check-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              <SpellCheck className="h-5 w-5 text-amber-600" /> Forslag fra staveretter
            </DialogTitle>
            <DialogDescription>
              Gjennomgå forslaget før du godtar. AI-en endrer ikke betydning eller dialekt.
            </DialogDescription>
          </DialogHeader>

          {result && (
            <div className="space-y-3">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1">Originalt</p>
                <div className="bg-stone-50 border border-stone-200 rounded-md p-2.5 text-sm text-stone-600 whitespace-pre-wrap" data-testid="spell-check-original">
                  {result.original}
                </div>
              </div>

              <div>
                <p className="text-[11px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">Forslag</p>
                <div className="bg-emerald-50 border border-emerald-200 rounded-md p-2.5 text-sm text-stone-900 whitespace-pre-wrap" data-testid="spell-check-corrected">
                  {result.corrected}
                </div>
              </div>

              {(result.changes || []).length > 0 && (
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-1">Endringer ({result.changes.length})</p>
                  <ul className="space-y-0.5 max-h-36 overflow-y-auto" data-testid="spell-check-changes">
                    {result.changes.map((c, i) => (
                      <li key={`${c.before}-${c.after}-${i}`} className="flex items-center gap-2 text-xs">
                        <span className="text-stone-400 line-through">{c.before}</span>
                        <span className="text-stone-300">→</span>
                        <span className="text-emerald-700 font-medium">{c.after}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex gap-2 pt-2 border-t border-stone-100">
                <button
                  onClick={accept}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-md bg-emerald-600 text-white px-4 py-2 text-sm font-semibold hover:bg-emerald-700"
                  data-testid="spell-check-accept"
                >
                  <Check className="h-4 w-4" /> Godta endringer
                </button>
                <button
                  onClick={dismiss}
                  className="inline-flex items-center justify-center gap-1.5 rounded-md border border-stone-200 bg-white px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50"
                  data-testid="spell-check-dismiss"
                >
                  <X className="h-4 w-4" /> Behold original
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
